import { Alert, Box, Button, Container, Title, Text } from "@mantine/core";
import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { useWelcomePage } from "../hooks/useWelcomePage";
import { WelcomeDashboard } from "./WelcomeDashboard";
import { resolveLabel } from "../../../shared/utils/i18n";
import { hasPermission } from "../../../shared/utils/permissions";
import { useScopedCustomerId } from "../../../shared/hooks/useScopedCustomerId";

export function WelcomePage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { user } = useAuth();
  const { customer } = useParams({ strict: false });
  const customerId = useScopedCustomerId();

  const {
    variant,
    locations,
    primaryLocation,
    jwtPayload,
    loadingRoles,
    loadingLocations,
    recentRequests,
    loadingRequests,
    userDocumentTypes,
  } = useWelcomePage(user?.id, customerId);

  if (!user) {
    return (
      <Container size="sm" py="xl">
        <Alert color="red" mb="md">
          <Title order={2} size="h4" mb="xs">
            {t("welcome.accessDenied")}
          </Title>
          <Text size="sm">{t("welcome.pleaseSignIn")}</Text>
        </Alert>
        <Button
          component={Link}
          to="/login"
          mt="md"
          data-testid="welcome-sign-in"
        >
          {t("welcome.goSignIn")}
        </Button>
      </Container>
    );
  }

  const basePath = `/app/c/${customer ?? ""}`;
  const requestsBasePath = `${basePath}/requests`;
  const documentTypes = userDocumentTypes.map((dt) => ({
    id: dt.id,
    name: resolveLabel(dt.name, dt.translations, locale),
    createHref: `${basePath}/requests/new?documentTypeId=${dt.id}`,
    // smell: two paths to the same listing: `/requests?documentTypeId=` from the
    // welcome page and `/d/:docTypeSlug` from the menu.
    viewHref: `${basePath}/requests?documentTypeId=${dt.id}`,
  }));
  const orderTrackingHref = hasPermission(user, "tracking.orderTracking.read")
    ? `${basePath}/tracking`
    : null;

  return (
    <Box py="xl">
      <WelcomeDashboard
        userName={user.name}
        primaryLocation={primaryLocation}
        variant={variant}
        locations={locations}
        recentRequests={recentRequests}
        documentTypes={documentTypes}
        requestsBasePath={requestsBasePath}
        jwtPayload={jwtPayload}
        isLoadingLocations={loadingRoles || loadingLocations}
        isLoadingRequests={loadingRequests}
        orderTrackingHref={orderTrackingHref}
      />
    </Box>
  );
}
